import {
  CustomFieldType,
  IDataFieldValue,
  IFieldConfig,
} from "@shared/lib/types/briefInfluencerV2";
import { Popconfirm } from "@shared/ui";
import { Plus, Trash2 } from "lucide-react";
import {
  getCategorySelectionState,
  groupPerformanceFieldsByCategory,
} from "../lib/groupPerformanceFieldsByCategory";
import styles from "../styles.module.scss";
import { BriefCustomFieldInlineView } from "./BriefCustomFieldInlineView";
import { BriefCustomFieldsForm } from "./BriefCustomFieldsForm";
import { getFieldSubtitle, HubCheckboxRow } from "./HubCheckboxRow";
import { HubCollapsibleSection } from "./HubCollapsibleSection";

interface PerformanceMetricsSectionProps {
  fields: IFieldConfig[];
  selectedCount: number;
  isVisible: (field: IFieldConfig) => boolean;
  onToggle: (field: IFieldConfig) => void;
  onToggleCategory: (fields: IFieldConfig[]) => void;
}

export function PerformanceMetricsSection({
  fields,
  selectedCount,
  isVisible,
  onToggle,
  onToggleCategory,
}: PerformanceMetricsSectionProps) {
  const groupedFields = groupPerformanceFieldsByCategory(fields);

  return (
    <HubCollapsibleSection
      title="Performance Metrics"
      defaultOpen
      summary={`${selectedCount} selected`}
    >
      {fields.length === 0 ? (
        <p className={styles.hubEmptyState}>No performance metrics available.</p>
      ) : (
        <div className={styles.hubSectionContent}>
          {groupedFields.map(({ category, fields: categoryFields }) => {
            const categoryState = getCategorySelectionState(
              categoryFields,
              isVisible
            );

            return (
              <div key={category} className={styles.metricCategoryGroup}>
                <HubCheckboxRow
                  label={category}
                  checked={categoryState.checked}
                  indeterminate={categoryState.indeterminate}
                  onToggle={() => onToggleCategory(categoryFields)}
                />
                <div className={styles.metricCategoryItems}>
                  {categoryFields.map((field) => (
                    <HubCheckboxRow
                      key={field._id || field.key}
                      label={field.displayName || field.key}
                      checked={isVisible(field)}
                      onToggle={() => onToggle(field)}
                      nested
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </HubCollapsibleSection>
  );
}

interface CustomColumnsSectionProps {
  title: string;
  infoTooltip: string;
  fields: IFieldConfig[];
  visibleCount: number;
  defaultOpen?: boolean;
  isCreateOpen: boolean;
  isCreateDisabled?: boolean;
  expandedFieldKey: string | null;
  isVisible: (field: IFieldConfig) => boolean;
  onToggle: (field: IFieldConfig) => void;
  onToggleCreate: () => void;
  onToggleFieldView: (field: IFieldConfig) => void;
  onCloseFieldView: () => void;
  onDelete?: (field: IFieldConfig) => void;
  isDeleting?: (fieldId?: string) => boolean;
  fieldName: string;
  fieldType: CustomFieldType;
  fieldDefault: IDataFieldValue | undefined;
  dropdownOptions: string[];
  isSubmitting: boolean;
  onFieldNameChange: (value: string) => void;
  onFieldTypeChange: (type: CustomFieldType) => void;
  onFieldDefaultChange: (
    value: string | number | string[] | undefined
  ) => void;
  onDropdownOptionsChange: (options: string[]) => void;
  onAddOption: () => void;
  onRemoveOption: (index: number) => void;
  onOptionChange: (index: number, value: string) => void;
  onSubmit: (event: React.FormEvent) => void;
  onCancelCreate: () => void;
}

export function CustomColumnsSection({
  title,
  infoTooltip,
  fields,
  visibleCount,
  defaultOpen = false,
  isCreateOpen,
  isCreateDisabled = false,
  expandedFieldKey,
  isVisible,
  onToggle,
  onToggleCreate,
  onToggleFieldView,
  onCloseFieldView,
  onDelete,
  isDeleting,
  fieldName,
  fieldType,
  fieldDefault,
  dropdownOptions,
  isSubmitting,
  onFieldNameChange,
  onFieldTypeChange,
  onFieldDefaultChange,
  onDropdownOptionsChange,
  onAddOption,
  onRemoveOption,
  onOptionChange,
  onSubmit,
  onCancelCreate,
}: CustomColumnsSectionProps) {
  return (
    <HubCollapsibleSection
      title={title}
      defaultOpen={defaultOpen}
      summary={
        fields.length === 0
          ? "No columns"
          : `${visibleCount}/${fields.length} visible`
      }
      infoTooltip={infoTooltip}
    >
      <button
        type="button"
        onClick={onToggleCreate}
        disabled={isCreateDisabled}
        className={styles.createButton}
      >
        <Plus size={14} /> Add a Custom Column
      </button>

      {isCreateOpen ? (
        <div className={styles.inlineFormWrap}>
          <BriefCustomFieldsForm
            fieldName={fieldName}
            fieldType={fieldType}
            fieldDefault={fieldDefault}
            dropdownOptions={dropdownOptions}
            isEditing={false}
            isSubmitting={isSubmitting}
            onFieldNameChange={onFieldNameChange}
            onFieldTypeChange={onTypeChange(
              onFieldTypeChange,
              onFieldDefaultChange,
              onDropdownOptionsChange
            )}
            onFieldDefaultChange={onFieldDefaultChange}
            onDropdownOptionsChange={onDropdownOptionsChange}
            onAddOption={onAddOption}
            onRemoveOption={onRemoveOption}
            onOptionChange={onOptionChange}
            onSubmit={onSubmit}
            onCancel={onCancelCreate}
          />
        </div>
      ) : null}

      {fields.length === 0 && !isCreateOpen ? (
        <p className={styles.hubEmptyState}>No columns yet.</p>
      ) : fields.length > 0 && !isCreateOpen ? (
        <div className={styles.hubSectionContent}>
          {fields.map((field) => {
            const isExpanded = expandedFieldKey === field.key;

            return (
              <div key={field._id || field.key} className={styles.fieldRowGroup}>
                <HubCheckboxRow
                  label={field.displayName || field.key}
                  subtitle={getFieldSubtitle(field)}
                  checked={isVisible(field)}
                  onToggle={() => onToggle(field)}
                  onLabelClick={() => onToggleFieldView(field)}
                  isActive={isExpanded}
                  action={
                    onDelete ? (
                      <Popconfirm
                        title="Delete Field"
                        description="Are you sure you want to delete this field?"
                        onConfirm={() => onDelete(field)}
                        okText="Delete"
                        cancelText="Cancel"
                        okButtonProps={{ danger: true }}
                      >
                        <button
                          type="button"
                          className={styles.deleteButton}
                          title="Delete"
                          disabled={isDeleting?.(field._id)}
                          onClick={(event) => event.stopPropagation()}
                        >
                          <Trash2 size={12} />
                        </button>
                      </Popconfirm>
                    ) : null
                  }
                />
                {isExpanded ? (
                  <BriefCustomFieldInlineView
                    field={field}
                    onClose={onCloseFieldView}
                    onDelete={onDelete}
                    isDeleting={isDeleting?.(field._id)}
                  />
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}
    </HubCollapsibleSection>
  );
}

function onTypeChange(
  onFieldTypeChange: (type: CustomFieldType) => void,
  onFieldDefaultChange: (
    value: string | number | string[] | undefined
  ) => void,
  onDropdownOptionsChange: (options: string[]) => void
) {
  return (type: CustomFieldType) => {
    onFieldTypeChange(type);
    onFieldDefaultChange(undefined);
    onDropdownOptionsChange([""]);
  };
}

export const BRIEF_TABLE_COLUMN_OPTIONS = [
  // { key: "contact", displayName: "Contact" },
  // { key: "reachoutStatus", displayName: "Reachout status" },
  // { key: "pricingPoc", displayName: "Pricing POC" },
  { key: "createdAt", displayName: "Created At" },
  { key: "updatedAt", displayName: "Updated At" },
];

interface BriefTableColumnsSectionProps {
  selectedKeys: string[];
  isVisible: (key: string) => boolean;
  onToggle: (key: string) => void;
}

export function BriefTableColumnsSection({
  selectedKeys,
  isVisible,
  onToggle,
}: BriefTableColumnsSectionProps) {
  return (
    <HubCollapsibleSection
      title="Brief Table Columns"
      defaultOpen
      summary={`${selectedKeys.length}/${BRIEF_TABLE_COLUMN_OPTIONS.length} visible`}
    >
      <div className={styles.hubSectionContent}>
        {BRIEF_TABLE_COLUMN_OPTIONS.map((col) => (
          <HubCheckboxRow
            key={col.key}
            label={col.displayName}
            checked={isVisible(col.key)}
            onToggle={() => onToggle(col.key)}
          />
        ))}
      </div>
    </HubCollapsibleSection>
  );
}
